import { Close } from '@ahamove/icons';
import type { EstimateOrderResponse } from '@ahm/api-wrappers-core';
import { formatCurrency } from '@ahm/common-helpers';
/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Stack,
  Text,
  Title,
  rem,
} from '@mantine/core';
import {
  closeAllModals,
  closeModal,
  type ContextModalProps,
} from '@mantine/modals';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { ModalName } from '@/components/modals';
import { useOptimizedRouteHook } from '@/hooks';
import { assetPrefix } from '@/lib/constants';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

type ModalOptimizeRoutesProps = {
  estimatedShotestResult: EstimateOrderResponse;
  onCancel: () => void;
};

const ModalOptimizeRoutes = ({
  innerProps,
}: ContextModalProps<ModalOptimizeRoutesProps>) => {
  const { estimatedShotestResult, onCancel } = innerProps;

  const isMobile = useIsMobile();

  //   const { t } = useTranslation(['location', 'common']);
  const tCommon = useTranslations('common');
  const tBooking = useTranslations('booking');

  const { handleOptimizeRoutes } = useOptimizedRouteHook();
  const { estimatedData } = useBookingStore((state) => state);

  function handleCancleOptimizeRoute() {
    if (typeof onCancel === 'function') {
      onCancel();
      closeModal(ModalName.OptimizeRoute);
    }
  }

  if (!estimatedData?.data || !estimatedShotestResult.data) return null;
  return (
    <>
      <Group
        justify="center"
        w="100%"
        p={isMobile ? rem(12) : rem(24)}
        bg="white"
      >
        <ActionIcon
          size={44}
          c="neutral.7"
          variant="transparent"
          pos="absolute"
          top={0}
          left={isMobile ? 0 : 'calc(100% - 44px)'}
          onClick={() => {
            closeAllModals();
          }}
          style={{ outline: 'none !important' }}
        >
          <Close width={24} height={24} />
        </ActionIcon>

        <Image
          width={185}
          height={157}
          src={`${assetPrefix}/static/images/optimize_routes.webp`}
          alt="static/images/optimize_routes.webp"
        />
      </Group>

      <Box
        style={{
          flex: '1 1 auto',
          // ...(isMobile
          //   ? {
          //       width: '100%',
          //       position: 'fixed',
          //       overflowY: 'auto',
          //       overflowX: 'hidden',
          //       height: 'auto',
          //       top: 168,
          //       bottom: 0,
          //     }
          //   : {}),
        }}
        p={isMobile ? rem(12) : rem(24)}
        pb={rem(12)}
        pt={0}
        bg={'white'}
      >
        <Title ta="center" order={3} c="neutral.7">
          {tBooking('location.want_to_take_a_shorter_route')}
        </Title>
        <Group m={24} style={{ justifyContent: 'space-evenly' }}>
          <Stack gap={4}>
            <Title c="neutral.5" order={5}>
              {estimatedData?.data?.distance}km
            </Title>
            <Title c="neutral.5" order={5}>
              {formatCurrency(estimatedData?.data?.total_price)}
            </Title>
          </Stack>
          <Image
            width={28}
            height={14}
            src={`${assetPrefix}/static/icons/gradient_chevron_right.svg`}
            alt="static/icons/gradient_chevron_right.svg"
          />
          <Stack gap={4}>
            <Title c="green.6" order={4}>
              {estimatedShotestResult?.data?.distance}km
            </Title>
            <Title c="green.6" order={5}>
              {formatCurrency(estimatedShotestResult?.data?.total_price)}
            </Title>
          </Stack>
        </Group>
        <Text
          c="neutral.5"
          fw={700}
          ta="center"
          size="14px"
          style={{ whiteSpace: 'pre-line' }}
        >
          {tBooking.rich(
            'location.save_amount_right_away_with_this_new_route',
            {
              span: (chunks) => (
                <Text size="16px" component="span" fw={700} c="green.6">
                  {chunks}
                </Text>
              ),
              amount: formatCurrency(
                estimatedData?.data?.total_price -
                  estimatedShotestResult?.data?.total_price
              ),
            }
          )}
        </Text>
      </Box>

      <Group wrap="nowrap" w="100%" p="md" bg="white">
        <Button
          variant="light"
          onClick={handleCancleOptimizeRoute}
          mih={48}
          size="sm"
          fw={700}
          fullWidth
        >
          {tCommon('no_tks')}
        </Button>
        <Button
          onClick={() => {
            handleOptimizeRoutes();
            closeModal(ModalName.OptimizeRoute);
          }}
          mih={48}
          fullWidth
          size="sm"
          fw={700}
        >
          {tCommon('agree')}
        </Button>
      </Group>
    </>
  );
};

export default ModalOptimizeRoutes;
